import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import {
  MdClose,
  MdHotelClass,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdPerson,
} from "../../customIcons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import Banner from "../Banner";
import CustomPopover from "../CustomPopover";
import GET_USER from "../../../queries/getUser";
import LOG_OUT from "../../../queries/LogOut";
import { Logo } from "../../atoms/Logo";
import Search from "./Search";
import ShoppingCartDrawer from "../../organisms/ShoppingCartDrawer";
import { brandsToExclude } from "../../../queries/toExclude";
import { fetchAllBlogCategories } from "../../../queries/fetchAllBlogCategories";
import { fetchAllBrands } from "../../../queries/fetchAllBrands";
import { navigate } from "gatsby";
import { parseHeaderBrands } from "../../utils/shopUtils";
import useAuth from "../../useAuth";

const Header = () => {
  const blogs = fetchAllBlogCategories();

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [blogIsOpen, setBlogIsOpen] = useState(false);

  const { loggedIn, user, isCustomerDefault, isBankAcc } = useAuth();

  const [logOut] = useMutation(LOG_OUT, {
    refetchQueries: [{ query: GET_USER }],
    onCompleted: () => {
      navigate("/sign-in");
    },
  });

  const brands = fetchAllBrands();
  const handleClickOutside = (): void => {
    setBlogIsOpen(false);
    setProfileMenuOpen(false);
  };
  const ref = useRef(null);

  useOutsideClick({
    ref,
    handler: handleClickOutside,
  });

  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;

      setVisible(currentScrollPos < 113 || prevScrollPos > currentScrollPos);

      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollPos]);

  // EXCLUDE BRANDS
  const { data: userData } = useQuery(GET_USER);

  const isUnlicensedUser = userData?.viewer?.capabilities?.includes(
    "customer_unlicensed",
  );

  const filteredBrands = React.useMemo(() => {
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      return brands.filter((item: any) => {
        return !brandsToExclude.includes(item.name);
      });
    }

    return brands.filter((item: any) => {
      return (
        item.name !== "Unlicensed" &&
        item.name !== "Medical Devices" &&
        item.name !== "Pharmaceuticals"
      );
    });
  }, [isUnlicensedUser, loggedIn, isCustomerDefault, isBankAcc, brands]);

  const parsedHeaderBrands = useMemo(() => {
    return parseHeaderBrands(filteredBrands);
  }, [filteredBrands]);
  const parsedHeaderBlogs = useMemo(() => {
    const blog = parseHeaderBrands(blogs);

    return blog
      .filter(
        (item: any) =>
          item.startsWith !== "Uncategorized" &&
          item.startsWith !== "PDO Threads" &&
          item.startsWith !== "Skin Care",
      )
      .map((item: any) => ({
        ...item,
        items: item.items.filter(
          (brand: any) =>
            brand.name !== "Uncategorized" &&
            brand.name !== "PDO Threads" &&
            brand.name !== "Skin Care",
        ),
      }))
      .filter((item: any) => item.items.length > 0);
  }, [blogs]);

  useEffect(() => {
    if (blogIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [blogIsOpen]);
  return (
    <>
      <VStack
        spacing="0px"
        top={0}
        zIndex={1000}
        opacity={visible ? 1 : 0}
        transform={`translateY(${visible ? "0px" : "0px"})`}
        transition="top 0.5s ease-in-out"
        style={{ display: visible ? "block" : "none" }}
        position="fixed"
        width="100%"
      >
        <Banner />
        <Stack
          as="nav"
          minH="80px"
          mx="auto"
          bg="#BB5D82"
          width="100%"
          px={{ base: "12px", sm: "20px" }}
          alignItems="center"
          flexDirection={{ base: "column", mm: "row" }}
          borderBottom="1px solid #FFFFFF3D"
        >
          <HStack w="100%" justifyContent="space-between" height="80px">
            <Link href="https://www.medsupplysolutions.com/">
              <Logo
                alt="Logo"
                textColor="#DBA4B9"
                dotsColor="#DBA4B9"
                color="#fff"
                mr={{ base: "4px" }}
                height="40px"
                width="148px"
                my={{ base: "8px", mm: "0px" }}
              />
            </Link>

            <Flex px={6}>
              <CustomPopover
                bgColor="#3B2060"
                hoverBg="#6A3AAB"
                baseUrl="/"
                title="Products"
                withIcon
                secondPanelItems={parsedHeaderBrands}
              />
            </Flex>
            <HStack width="100%" position="relative">
              <Search />

              {!loggedIn ? (
                <Link href="https://www.medsupplysolutions.com/sign-in/">
                  <Button
                    ml="24px"
                    mr="16px"
                    leftIcon={
                      <MdPerson width="16px" height="16px" fill="#fff" />
                    }
                    color="#ffff"
                    fontWeight="600"
                    variant="outline"
                    borderColor="#ffff"
                    background="transparent"
                    _hover={{ background: "whiteAlpha.100" }}
                  >
                    Log In
                  </Button>
                </Link>
              ) : (
                <Box position="relative">
                  <Button
                    ml="32px"
                    mr="16px"
                    leftIcon={
                      <MdPerson width="16px" height="16px" fill="#fff" />
                    }
                    color="#ffff"
                    fontWeight="600"
                    variant="outline"
                    borderColor="#ffff"
                    background="transparent"
                    _hover={{ background: "whiteAlpha.100" }}
                    onClick={() => {
                      setProfileMenuOpen(!profileMenuOpen);
                    }}
                  >
                    {user?.firstName}
                    <MdKeyboardArrowDown fill="#fff" />
                  </Button>

                  {profileMenuOpen && (
                    <VStack
                      position="absolute"
                      bottom="-88px"
                      right="0"
                      backgroundColor="white"
                      width="230px"
                      borderRadius="6px"
                      spacing={0}
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
                      ref={ref}
                    >
                      <Link href={"/my-account/"}>
                        <Button
                          backgroundColor="white"
                          width="230px"
                          justifyContent="flex-start"
                          p="12px"
                          borderRadius={0}
                          _hover={{ backgroundColor: "#FFE9F3" }}
                          onClick={(e) => e.stopPropagation()}
                          borderTopLeftRadius="6px"
                          borderTopRightRadius="6px"
                        >
                          <Text
                            color="#4A5568"
                            fontSize={14}
                            textAlign="left"
                            fontWeight="400"
                          >
                            My account
                          </Text>
                        </Button>
                      </Link>

                      <Button
                        backgroundColor="white"
                        width="100%"
                        justifyContent="flex-start"
                        p="12px"
                        borderRadius={0}
                        _hover={{ backgroundColor: "#FFE9F3" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          logOut();
                        }}
                        borderBottomLeftRadius="6px"
                        borderBottomRightRadius="6px"
                      >
                        <Text
                          color="#4A5568"
                          fontSize={14}
                          textAlign="left"
                          fontWeight="400"
                        >
                          Log out
                        </Text>
                      </Button>
                    </VStack>
                  )}
                </Box>
              )}
              <ShoppingCartDrawer
                boxWidth="40px"
                boxHeight="40px"
                border="1px solid #fff"
                width="16px"
                height="16px"
                placement="right"
                hoverBg="whiteAlpha.100"
                iconColor="#fff"
                px="12px"
                pt="8px"
                borderRadius="6px"
              />
            </HStack>
          </HStack>
        </Stack>
        <Stack
          h="40px"
          mx="auto"
          bg="#BB5D82"
          width="100%"
          px={{ base: "12px", sm: "20px" }}
          alignItems="center"
          flexDirection={{ base: "column", mm: "row" }}
        >
          <Link href="https://www.medsupplysolutions.com/about-us/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
            >
              About
            </Button>
          </Link>
          <Link href="https://www.medsupplysolutions.com/promo/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
              rightIcon={<MdHotelClass />}
            >
              Promotions
            </Button>
          </Link>
          <Link href="https://www.medsupplysolutions.com/referral/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
            >
              Referral Program
            </Button>
          </Link>
          <Box position="relative" ref={ref}>
            <Button
              fontWeight="600"
              bg="#BB5D82"
              color="white"
              _hover={{ background: "whiteAlpha.100" }}
              rightIcon={
                blogIsOpen ? (
                  <MdKeyboardArrowUp fill="#fff" />
                ) : (
                  <MdKeyboardArrowDown fill="fff" />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                setBlogIsOpen(!blogIsOpen);
              }}
            >
              Blog
            </Button>

            {blogIsOpen && (
              <>
                {blogs.length <= 30 ? (
                  <Stack
                    position="absolute"
                    top="12"
                    left="-28"
                    backgroundColor="white"
                    borderRadius="6px"
                    spacing={3}
                    p="20px"
                    justifyContent="flex-start"
                    w="446px"
                    alignItems="flex-start"
                    boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
                  >
                    <Link
                      color="#BB5D82"
                      fontWeight="700"
                      href="https://www.medsupplysolutions.com/blog/"
                    >
                      Visit blog page
                    </Link>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {blogs.map((blog: any) => {
                        return blog.name !== "Uncategorized" &&
                          blog.name !== "PDO Threads" &&
                          blog.name !== "Skin Care" ? (
                          <Stack key={blog.id} h="27px">
                            <Link
                              href={`https://www.medsupplysolutions.com/blog/${blog.slug}/`}
                            >
                              <Text>{blog.name}</Text>
                            </Link>
                          </Stack>
                        ) : null;
                      })}
                    </SimpleGrid>
                  </Stack>
                ) : (
                  <Stack
                    position="fixed"
                    top="154px"
                    left="0"
                    right="0"
                    bottom="0"
                    h="100vh"
                    gap={0}
                    w="100%"
                    overflowY="scroll"
                    bg="#F7FAFC"
                  >
                    <Stack overflowY="scroll" w="100%" mb="160px" gap={0}>
                      <HStack
                        borderBottom="1px solid #E1E7EF"
                        bg="#fff"
                        justifyContent="space-between"
                      >
                        <HStack
                          cursor="pointer"
                          gap={0}
                          py={5}
                          px={6}
                          onClick={() => setBlogIsOpen(false)}
                        >
                          <MdClose />
                          <Text
                            fontSize="18px"
                            fontWeight="500"
                            color="#1A212D"
                            px={4}
                          >
                            Blog categories
                          </Text>
                        </HStack>

                        <svg
                          style={{ marginRight: "24px" }}
                          xmlns="http://www.w3.org/2000/svg"
                          width="84"
                          height="44"
                          viewBox="0 0 84 44"
                          fill="none"
                        >
                          <g clipPath="url(#clip0_5478_47525)">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M26.4216 1.76985C27.0522 1.76985 27.5687 1.2493 27.5687 0.613684C27.5687 -0.0219325 27.0522 -0.54248 26.4216 -0.54248C25.7909 -0.54248 25.2745 -0.0219325 25.2745 0.613684C25.2745 1.2493 25.7909 1.76985 26.4216 1.76985ZM5.74099 7.56163C5.74099 6.28492 6.77937 5.24382 8.04064 5.24382C9.30736 5.24382 10.3403 6.28492 10.3403 7.56163C10.3403 8.83834 9.30736 9.87944 8.04064 9.87944C6.77393 9.87944 5.74099 8.83834 5.74099 7.56163ZM0 16.8164C0 16.1808 0.516471 15.6603 1.14711 15.6603C1.77775 15.6603 2.29422 16.1808 2.29422 16.8164C2.29422 17.452 1.77775 17.9726 1.14711 17.9726C0.516471 17.9726 0 17.452 0 16.8164ZM0 26.0822C0 25.4466 0.516471 24.926 1.14711 24.926C1.77775 24.926 2.29422 25.4466 2.29422 26.0822C2.29422 26.7178 1.77775 27.2383 1.14711 27.2383C0.516471 27.2383 0 26.7178 0 26.0822ZM17.2339 41.1342C16.6032 41.1342 16.0867 41.6548 16.0867 42.2904C16.0867 42.926 16.6032 43.4466 17.2339 43.4466C17.8645 43.4466 18.381 42.926 18.381 42.2904C18.381 41.6548 17.8645 41.1342 17.2339 41.1342ZM8.04064 14.5041C6.77937 14.5041 5.74099 15.5452 5.74099 16.8219C5.74099 18.0986 6.77393 19.1397 8.04064 19.1397C9.30736 19.1397 10.3403 18.0986 10.3403 16.8219C10.3403 15.5452 9.30736 14.5041 8.04064 14.5041ZM5.74099 26.0822C5.74099 24.8055 6.77937 23.7644 8.04064 23.7644C9.30736 23.7644 10.3403 24.8055 10.3403 26.0822C10.3403 27.3589 9.30736 28.4 8.04064 28.4C6.77393 28.4 5.74099 27.3589 5.74099 26.0822ZM19.5335 7.55615C19.5335 8.83286 18.4951 9.87396 17.2338 9.87396C15.9671 9.87396 14.9342 8.83286 14.9342 7.55615C14.9342 6.27944 15.9671 5.23834 17.2338 5.23834C18.5005 5.23834 19.5335 6.27944 19.5335 7.55615ZM17.2339 1.76985C17.8645 1.76985 18.381 1.2493 18.381 0.613684C18.381 -0.0219325 17.8645 -0.54248 17.2339 -0.54248C16.6032 -0.54248 16.0867 -0.0219325 16.0867 0.613684C16.0867 1.2493 16.6032 1.76985 17.2339 1.76985ZM28.7212 7.55615C28.7212 8.83286 27.6829 9.87396 26.4216 9.87396C25.1549 9.87396 24.1219 8.83286 24.1219 7.55615C24.1219 6.27944 25.1549 5.23834 26.4216 5.23834C27.6883 5.23834 28.7212 6.27944 28.7212 7.55615ZM8.04064 33.0301C6.77937 33.0301 5.74099 34.0712 5.74099 35.3479C5.74099 36.6246 6.77393 37.6657 8.04064 37.6657C9.30736 37.6657 10.3403 36.6246 10.3403 35.3534C10.3403 34.0822 9.30736 33.0356 8.04064 33.0356V33.0301ZM33.3151 35.3479C33.3151 34.0712 34.3535 33.0301 35.6148 33.0301V33.0356C36.8815 33.0356 37.9145 34.0822 37.9145 35.3534C37.9145 36.6246 36.8761 37.6657 35.6148 37.6657C34.3481 37.6657 33.3151 36.6246 33.3151 35.3479ZM35.6148 5.24382C34.3535 5.24382 33.3151 6.28492 33.3151 7.56163C33.3151 8.83834 34.3481 9.87944 35.6148 9.87944C36.8815 9.87944 37.9145 8.83834 37.9145 7.56163C37.9145 6.28492 36.8815 5.24382 35.6148 5.24382ZM13.7871 26.0822C13.7871 24.1589 15.3256 22.6082 17.2338 22.6082C19.1421 22.6082 20.6806 24.1589 20.6806 26.0822C20.6806 28.0055 19.1421 29.5561 17.2338 29.5561C15.3256 29.5561 13.7871 28.0055 13.7871 26.0822ZM17.2338 33.0301C15.9725 33.0301 14.9342 34.0712 14.9342 35.3479C14.9342 36.6246 15.9671 37.6657 17.2338 37.6657C18.5005 37.6657 19.5335 36.6246 19.5335 35.3479C19.5335 34.0712 18.5005 33.0301 17.2338 33.0301ZM13.7871 16.8219C13.7871 14.8986 15.3256 13.3479 17.2338 13.3479C19.1421 13.3479 20.6806 14.8986 20.6806 16.8219C20.6806 18.7452 19.1421 20.2959 17.2338 20.2959C15.3256 20.2959 13.7871 18.7452 13.7871 16.8219ZM26.4216 33.0301C25.1603 33.0301 24.1219 34.0712 24.1219 35.3479C24.1219 36.6246 25.1549 37.6657 26.4216 37.6657C27.6883 37.6657 28.7212 36.6246 28.7212 35.3479C28.7212 34.0712 27.6883 33.0301 26.4216 33.0301ZM25.2745 42.2904C25.2745 41.6548 25.7909 41.1342 26.4216 41.1342C27.0522 41.1342 27.5687 41.6548 27.5687 42.2904C27.5687 42.926 27.0522 43.4466 26.4216 43.4466C25.7909 43.4466 25.2745 42.926 25.2745 42.2904Z"
                              fill="#CCD6E0"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M53.751 17.989C53.751 21.9385 57.1874 22.4769 59.8334 22.8914L59.8335 22.8914C60.0251 22.9214 60.2127 22.9508 60.3945 22.9808C61.7427 23.2 62.8083 23.4575 62.8083 24.274C62.7757 24.7781 62.4332 25.6 60.4271 25.6C59.019 25.6329 57.9806 25.189 57.9806 24.115H53.8108C53.8108 27.9671 57.0075 29.2986 60.4271 29.2986C63.8467 29.2986 67.4783 28.0657 67.5109 24.1808C67.4783 20.4822 64.1239 19.8849 61.4926 19.4411L61.4198 19.4288C59.7624 19.1497 58.421 18.9238 58.421 18.0219C58.421 17.4192 58.9864 16.9479 60.5195 16.9479C62.0526 16.9479 62.5582 17.4246 62.5582 18.2411H66.7607C66.7607 15.463 64.7872 12.8712 60.177 12.8712C56.665 12.8712 53.751 14.3561 53.751 17.989ZM25.1929 13.5068V29.3041H29.7704V20.8383C29.7704 18.7835 30.7762 17.4246 32.717 17.4246C34.5057 17.4246 35.506 18.5315 35.506 20.8986V29.3041H40.0836V20.8383C40.0836 18.7835 41.1817 17.4246 43.1552 17.4246C44.9112 17.4246 46.1018 18.5315 46.1018 20.8986V29.3041H50.6794V19.5726C50.6794 15.0575 48.7059 13.189 45.3189 13.189C41.6221 13.189 39.8009 15.4027 39.5834 16.2246C38.7353 14.1096 37.2348 13.189 34.8482 13.189C31.0525 13.189 30.0353 15.5976 29.7871 16.1854L29.7704 16.2246V13.5068H25.1929ZM76.3224 22.8914C73.6764 22.4769 70.24 21.9385 70.24 17.989C70.24 14.3561 73.154 12.8712 76.666 12.8712C81.2762 12.8712 83.2497 15.463 83.2497 18.2411H79.0472C79.0472 17.4246 78.5416 16.9479 77.0085 16.9479C75.4754 16.9479 74.91 17.4192 74.91 18.0219C74.91 18.9238 76.2514 19.1497 77.9088 19.4288L77.9817 19.4411C80.613 19.8849 83.9673 20.4822 83.9999 24.1808C83.9673 28.0657 80.3357 29.2986 76.9161 29.2986C73.4965 29.2986 70.2999 27.9671 70.2999 24.115H74.4697C74.4697 25.189 75.5081 25.6329 76.9161 25.6C78.9222 25.6 79.2647 24.7781 79.2973 24.274C79.2973 23.4575 78.2318 23.2 76.8835 22.9808C76.7017 22.9508 76.5141 22.9214 76.3224 22.8914Z"
                              fill="#CCD6E0"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_5478_47525">
                              <rect width="84" height="44" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </HStack>

                      <Link
                        py={5}
                        px={8}
                        color="#BB5D82"
                        fontWeight="700"
                        href="https://www.medsupplysolutions.com/blog/"
                      >
                        Visit blog page
                      </Link>

                      <Grid
                        padding="20px"
                        templateColumns={{
                          base: "repeat(1, 1fr)",
                          sm: "repeat(1, 1fr)",
                          md: "repeat(2, 1fr)",
                          lg: "repeat(3, 1fr)",
                          xl: "repeat(6, 1fr)",
                        }}
                        bg="#F7FAFC"
                        gap={6}
                      >
                        {parsedHeaderBlogs?.map((item: any, index: any) => {
                          return item.name !== "Uncategorized" &&
                            item.name !== "PDO Threads" &&
                            item.name !== "Skin Care" ? (
                            <VStack
                              maxW="235px"
                              key={index}
                              alignItems="flex-start"
                            >
                              <Text
                                fontSize="16px"
                                color="#3B2060"
                                fontWeight="700"
                                textAlign="start"
                                pl="4"
                              >
                                {item.startsWith}
                              </Text>

                              <VStack
                                borderTopWidth={1}
                                borderTopColor="#E2E8F0"
                                flex={1}
                                width="100%"
                                pt="6"
                                alignItems="flex-start"
                                pl="4"
                              >
                                {item.items.map((blog: any, index: any) => {
                                  return (
                                    <Link
                                      key={index}
                                      href={`https://www.medsupplysolutions.com/blog/${blog.slug}/`}
                                    >
                                      <Text
                                        _hover={{ textDecoration: "underline" }}
                                        fontSize="18px"
                                      >
                                        {blog.name}
                                      </Text>
                                    </Link>
                                  );
                                })}
                              </VStack>
                            </VStack>
                          ) : null;
                        })}
                      </Grid>
                    </Stack>
                  </Stack>
                )}
              </>
            )}
          </Box>

          <Link href="https://www.medsupplysolutions.com/contact/">
            <Button
              fontWeight="600"
              bg="#BB5D82"
              color="white"
              _hover={{ background: "whiteAlpha.100" }}
            >
              Contact
            </Button>
          </Link>
          <Link
            href="https://track.medsupplysolutions.com/"
            target="_blank"
            rel="noopener"
          >
            <Button
              fontWeight="600"
              variant="ghost"
              size="md"
              bg="#BB5D82"
              color="white"
              _hover={{ background: "whiteAlpha.100" }}
              rightIcon={
                <img
                  width="24"
                  height="24"
                  src="https://img.icons8.com/ios/50/FFFFFF/order-shipped.png"
                  alt="shipping icon"
                />
              }
            >
              Order tracker
            </Button>
          </Link>
        </Stack>
      </VStack>
    </>
  );
};

export default Header;
